
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useAuthMethods } from "@/hooks/auth/authentication/useAuthMethods";
import AuthForm from "@/components/auth/AuthForm";
import PasswordResetForm from "@/components/auth/PasswordResetForm";
import { EmailConfirmationDialog } from "@/components/auth/EmailConfirmationDialog";
import { supabase } from "@/lib/supabase";
import { getEnvironmentConfig } from "@/utils/auth-config";
import { useToast } from "@/hooks/use-toast";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [isLoading, setIsLoading] = useState(false);
  const [hasSetSession, setHasSetSession] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");
  
  const { signIn, signUp } = useAuthMethods(setIsLoading);

  useEffect(() => {
    console.log("Auth page mounted with params:", Object.fromEntries(searchParams.entries()));
    
    // Check if we should default to sign up mode
    const modeParam = searchParams.get("mode");
    const shouldShowSignUp = location.state?.showSignUp || 
                            location.state?.defaultTab === "signup" || 
                            modeParam === "signup";
    if (shouldShowSignUp) {
      setAuthMode("signup");
    }
    
    // Log environment information for debugging
    const envConfig = getEnvironmentConfig();
    console.log('Auth page environment config:', envConfig);
    
    // Check for password reset flow - prioritize reset parameter and recovery tokens
    const isReset = searchParams.get("reset") === "true";
    const hasResetTokens = searchParams.has("access_token") && searchParams.get("type") === "recovery";
    const hasAnyResetIndicator = isReset || hasResetTokens;
    
    // Check for email confirmation - look for Supabase confirmation tokens
    const isConfirmed = searchParams.get("confirmed") === "true";
    const hasConfirmationToken = searchParams.has("access_token") && 
      (searchParams.get("type") === "signup" || searchParams.get("type") === "email_change");
    
    console.log('Auth page URL params:', {
      isReset,
      hasResetTokens,
      hasAnyResetIndicator,
      isConfirmed,
      hasConfirmationToken,
      type: searchParams.get("type"),
      searchParams: Object.fromEntries(searchParams.entries())
    });
    
    // Handle password reset flow
    if (hasAnyResetIndicator) {
      console.log("Password reset flow detected, showing PasswordResetForm");
      setIsPasswordReset(true);
      
      // If we have reset tokens in the URL, set the session
      if (hasResetTokens && !hasSetSession) {
        const accessToken = searchParams.get("access_token");
        const refreshToken = searchParams.get("refresh_token");
        
        console.log('Processing password reset tokens:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          type: searchParams.get("type")
        });
        
        if (accessToken && refreshToken) {
          // Set the session with the tokens from the URL
          supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          }).then(({ error }) => {
            if (error) {
              console.error("Error setting session:", error);
              toast({
                title: "Reset link expired",
                description: "Please request a new password reset link.",
                variant: "destructive",
              });
              setIsPasswordReset(false);
            } else {
              console.log("Password reset session established successfully");
              setHasSetSession(true);
            }
          });
        }
      }
    } else if (hasConfirmationToken && !hasSetSession) {
      // Handle Supabase email confirmation tokens
      const accessToken = searchParams.get("access_token");
      const refreshToken = searchParams.get("refresh_token");
      
      console.log('Processing email confirmation tokens:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        type: searchParams.get("type")
      });
      
      if (accessToken && refreshToken) {
        // Set the session with the tokens from the URL
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).then(({ error }) => {
          if (error) {
            console.error("Error setting confirmation session:", error);
            toast({
              title: "Confirmation failed",
              description: "The confirmation link is invalid or has expired. Please try signing up again.",
              variant: "destructive",
            });
          } else {
            console.log("Email confirmation successful, redirecting to dashboard");
            setHasSetSession(true);
            toast({
              title: "Email confirmed!",
              description: "Your account has been activated successfully.",
            });
            navigate("/dashboard", { replace: true });
          }
        });
      }
    } else if (isConfirmed) {
      // User clicked confirmation link, redirect to dashboard
      console.log("Email confirmation detected, redirecting to dashboard");
      toast({
        title: "Email confirmed!",
        description: "Your account has been activated successfully.",
      });
      navigate("/dashboard", { replace: true });
    } else if (user && user.email_confirmed_at && !isPasswordReset) {
      // Only redirect to dashboard if user is authenticated AND confirmed AND not in password reset flow
      console.log("User authenticated and confirmed, redirecting to dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate, searchParams, hasSetSession, isPasswordReset, toast, location.state]);

  const handleSubmit = async (data: any) => {
    console.log("Auth form submitted:", { mode: authMode, email: data.email });
    
    if (authMode === "login") {
      return await signIn(data.email, data.password);
    } else {
      const result = await signUp(data);
      console.log("Signup result:", result);
      
      // Show confirmation dialog if signup successful and needs confirmation
      if (!result.error && result.needsConfirmation) {
        console.log("Showing confirmation dialog for:", data.email);
        setConfirmationEmail(data.email);
        setShowConfirmationDialog(true);
      } else if (!result.error && !result.needsConfirmation) {
        // If no confirmation needed, redirect to dashboard
        console.log("No confirmation needed, redirecting to dashboard");
        navigate("/dashboard", { replace: true });
      }
      
      return result;
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === "login" ? "signup" : "login");
  };

  const handlePasswordResetSuccess = () => {
    // Only navigate to dashboard after successful password reset
    console.log("Password reset successful, navigating to dashboard");
    navigate("/dashboard", { replace: true });
  };

  const handleCloseConfirmationDialog = () => {
    console.log("Closing confirmation dialog");
    setShowConfirmationDialog(false);
    setConfirmationEmail("");
  };

  // Show password reset form if we're in password reset flow
  if (isPasswordReset) {
    console.log("Rendering PasswordResetForm component");
    return <PasswordResetForm onSuccess={handlePasswordResetSuccess} />;
  }

  return (
    <div className="container flex items-center justify-center min-h-screen py-8">
      <AuthForm 
        type={authMode}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        onToggleForm={toggleAuthMode}
      />
      
      <EmailConfirmationDialog
        isOpen={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        userEmail={confirmationEmail}
      />
    </div>
  );
};

export default Auth;
